import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Link as LinkIcon, Sparkles, Trash2, ExternalLink,
  Play, ChevronDown, ChevronRight,
} from "lucide-react";
import {
  analyzeReferenceVideo,
  getReferenceVideo,
  listReferenceVideos,
  deleteReferenceVideo,
  spawnStoryboardFromReferenceSeed,
  generateMoreReferenceSeeds,
  dismissReferenceSeed,
} from "@/lib/api";

/**
 * ReferenceVideoAnalyzer — the "paste a YouTube URL, get a whole
 * storyboard seed pack" panel. Sits on the scoped Storyboards page
 * next to the Idea Engine. Analyzes: visual style (via GPT-5.2
 * Vision on the video's frames), transcript beat-breakdown, and 5
 * recast idea seeds scoped to THIS channel. Each seed is one-click
 * spawnable into a real storyboard with the reference's style
 * prompt-prefix pre-applied.
 */
export default function ReferenceVideoAnalyzer({ handle, channelLabel, onStoryboardSpawned }) {
  const [url, setUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [references, setReferences] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const navigate = useNavigate();

  const loadList = useCallback(async () => {
    if (!handle) return;
    setLoadingList(true);
    try {
      const rows = await listReferenceVideos(handle);
      setReferences(rows);
    } catch (err) {
      // Silent — this list is a "nice to have" on page load.
      // Empty state renders anyway.
    } finally {
      setLoadingList(false);
    }
  }, [handle]);

  useEffect(() => { loadList(); }, [loadList]);

  const onAnalyze = async (e) => {
    e?.preventDefault?.();
    if (!url.trim() || analyzing) return;
    setAnalyzing(true);
    try {
      // 1) Kick off background job — returns immediately with the
      //    `status: 'running'` stub.
      const stub = await analyzeReferenceVideo({
        url: url.trim(),
        channelHandle: handle,
      });
      // Show the stub straight away so the user sees "running…"
      setReferences((prev) => [
        stub,
        ...prev.filter((r) => r.reference_id !== stub.reference_id),
      ]);
      setUrl("");
      // 2) Poll until the backend flips status → complete / failed.
      //    Every 3s, max ~5min so a wedged pod doesn't spin forever.
      const refId = stub.reference_id;
      const started = Date.now();
      let final = null;
      while (Date.now() - started < 5 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const fresh = await getReferenceVideo(refId);
          setReferences((prev) => prev.map(
            (r) => (r.reference_id === refId ? fresh : r),
          ));
          if (fresh?.status === "complete") { final = fresh; break; }
          if (fresh?.status === "failed") {
            throw new Error(fresh.error || "Analysis failed");
          }
        } catch (pollErr) {
          // 404 during poll means the row was deleted mid-flight —
          // stop and bubble.
          if (pollErr?.response?.status === 404) throw pollErr;
          // Any other transient (5xx, network blip) — keep polling.
        }
      }
      if (final) {
        toast.success("Reference analyzed — style + 5 idea seeds ready");
      } else {
        toast.error("Analysis is taking longer than expected — check back in a bit.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Analysis failed";
      const msg = detail === "invalid_youtube_url"
        ? "That doesn't look like a YouTube URL. Try a full https://youtube.com/watch?v=... or a https://youtu.be/... link."
        : `Couldn't analyze: ${detail}`;
      toast.error(msg);
    } finally {
      setAnalyzing(false);
    }
  };

  const onDelete = async (ref) => {
    if (!window.confirm(`Remove "${ref.video?.title || ref.url}" from your reference library?`)) return;
    try {
      await deleteReferenceVideo(ref.reference_id);
      setReferences((prev) => prev.filter((r) => r.reference_id !== ref.reference_id));
      toast.success("Reference removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete");
    }
  };

  const onSpawn = async (ref, seedIdx) => {
    try {
      const res = await spawnStoryboardFromReferenceSeed(ref.reference_id, seedIdx);
      toast.success(
        `Storyboard spawned${res.prefix_applied ? " — style prefix applied" : ""}`,
      );
      // Optimistically mark the seed as consumed on this reference so
      // the "Spawn storyboard" list drops it right away — the backend
      // already flipped the flag, but re-fetching just for that would
      // add a network round-trip the user doesn't need to wait for.
      setReferences((prev) => prev.map((r) => {
        if (r.reference_id !== ref.reference_id) return r;
        const seeds = Array.isArray(r.idea_seeds) ? [...r.idea_seeds] : [];
        if (seeds[seedIdx]) {
          seeds[seedIdx] = {
            ...seeds[seedIdx],
            consumed: true,
            spawned_storyboard_id: res.storyboard_id,
          };
        }
        return { ...r, idea_seeds: seeds };
      }));
      onStoryboardSpawned?.();
      if (res.storyboard_id) {
        navigate(`/storyboards/${res.storyboard_id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't spawn storyboard");
    }
  };

  const onDismissSeed = async (ref, seedIdx) => {
    // Optimistically hide the seed — feels instant. Roll back on error.
    const before = ref.idea_seeds || [];
    setReferences((prev) => prev.map((r) => {
      if (r.reference_id !== ref.reference_id) return r;
      const seeds = Array.isArray(r.idea_seeds) ? [...r.idea_seeds] : [];
      if (seeds[seedIdx]) {
        seeds[seedIdx] = {
          ...seeds[seedIdx],
          consumed: true,
          dismissed: true,
        };
      }
      return { ...r, idea_seeds: seeds };
    }));
    try {
      await dismissReferenceSeed(ref.reference_id, seedIdx);
    } catch (err) {
      // Roll back — put the seed back into the visible list.
      setReferences((prev) => prev.map((r) => (
        r.reference_id === ref.reference_id ? { ...r, idea_seeds: before } : r
      )));
      toast.error(err?.response?.data?.detail || "Couldn't dismiss idea");
    }
  };

  const onGenerateMore = async (ref) => {
    try {
      const res = await generateMoreReferenceSeeds(ref.reference_id, 10);
      const added = Number(res?.added || 0);
      if (added <= 0) {
        toast.info("GPT couldn't find any new angles — try analyzing another reference.");
        return;
      }
      // Append the fresh seeds onto the reference's idea_seeds so they
      // render straight underneath the existing (unconsumed) ones.
      setReferences((prev) => prev.map((r) => {
        if (r.reference_id !== ref.reference_id) return r;
        const existing = Array.isArray(r.idea_seeds) ? r.idea_seeds : [];
        return {
          ...r,
          idea_seeds: [...existing, ...(res.seeds || [])],
        };
      }));
      toast.success(`${added} more idea${added === 1 ? "" : "s"} ready`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = detail === "no_new_ideas"
        ? "GPT returned only duplicates — try again in a moment."
        : detail || "Couldn't generate more ideas";
      toast.error(msg);
    }
  };

  if (!handle) return null;

  return (
    <section
      data-testid="reference-video-analyzer"
      className="border border-yellow-400/30 bg-yellow-400/[0.03] rounded-sm p-5 mb-6 space-y-4"
    >
      <header className="space-y-1">
        <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-yellow-400">
          <Sparkles className="w-3.5 h-3.5" />
          // REFERENCE VIDEO ANALYZER
        </div>
        <h2 className="font-display text-2xl font-black text-white leading-snug">
          Emulate the style from any YouTube video
        </h2>
        <p className="text-xs text-zinc-400 max-w-2xl leading-relaxed">
          Paste a YouTube URL. We&apos;ll analyze the visual style, pull the
          transcript, and generate <span className="text-yellow-300 font-bold">5 recast idea seeds</span>
          {" "}for <span className="text-yellow-300 font-bold">{channelLabel || "this channel"}</span>.
          Every scene image + thumbnail on the spawned storyboards inherits
          the reference&apos;s look automatically.
        </p>
      </header>

      <form onSubmit={onAnalyze} className="flex flex-wrap gap-2 items-stretch">
        <div className="relative flex-1 min-w-[280px]">
          <LinkIcon className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            data-testid="reference-video-url-input"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=…"
            disabled={analyzing}
            className="w-full bg-black border border-zinc-800 hover:border-zinc-600 focus:border-yellow-400/60 focus:outline-none rounded-sm pl-9 pr-3 py-2.5 text-sm text-zinc-100 placeholder-zinc-600 disabled:opacity-50"
          />
        </div>
        <button
          type="submit"
          data-testid="reference-video-analyze-btn"
          disabled={analyzing || !url.trim()}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] bg-yellow-400 hover:bg-yellow-300 text-black font-black px-5 py-2.5 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {analyzing ? "Analyzing (~60s)…" : "Analyze reference"}
        </button>
      </form>

      {references.length === 0 && !loadingList && (
        <div className="text-[11px] text-zinc-500 font-mono border border-dashed border-zinc-800 rounded-sm p-4">
          No references yet. Analyze your first video above.
        </div>
      )}

      <div className="space-y-3">
        {references.map((ref) => (
          <ReferenceCard
            key={ref.reference_id}
            ref_={ref}
            onSpawn={(i) => onSpawn(ref, i)}
            onDelete={() => onDelete(ref)}
            onGenerateMore={() => onGenerateMore(ref)}
            onDismissSeed={(i) => onDismissSeed(ref, i)}
          />
        ))}
      </div>
    </section>
  );
}

function ReferenceCard({ ref_, onSpawn, onDelete, onGenerateMore, onDismissSeed }) {
  const [expanded, setExpanded] = useState(true);
  const [spawningIdx, setSpawningIdx] = useState(-1);
  const [generatingMore, setGeneratingMore] = useState(false);
  const style = ref_.style || {};
  const seeds = ref_.idea_seeds || [];
  // Keep the original index so `onSpawn(i)` still hits the right slot
  // in the backend's `idea_seeds` array — filtering the array here
  // would misalign indices after the first consumed seed.
  const availableSeeds = seeds
    .map((s, i) => ({ seed: s, idx: i }))
    .filter(({ seed }) => !seed?.consumed);
  const consumedCount = seeds.length - availableSeeds.length;
  const palette = Array.isArray(style.color_palette) ? style.color_palette.filter(Boolean).slice(0, 6) : [];

  const handleSpawn = async (i) => {
    setSpawningIdx(i);
    try { await onSpawn(i); } finally { setSpawningIdx(-1); }
  };

  const handleGenerateMore = async () => {
    if (generatingMore) return;
    setGeneratingMore(true);
    try { await onGenerateMore?.(); } finally { setGeneratingMore(false); }
  };

  return (
    <article
      data-testid={`reference-card-${ref_.reference_id}`}
      className="border border-zinc-800 bg-black rounded-sm overflow-hidden"
    >
      <header className="flex items-start gap-3 p-3">
        {ref_.video?.thumbnail_url && (
          <img
            src={ref_.video.thumbnail_url}
            alt=""
            className="w-32 h-18 object-cover border border-zinc-800 flex-shrink-0 rounded-sm"
          />
        )}
        <div className="flex-1 min-w-0 space-y-1">
          <div className="text-sm font-bold text-zinc-100 leading-tight line-clamp-2">
            {ref_.video?.title || ref_.url}
          </div>
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            <span>{ref_.video?.channel_name || "—"}</span>
            {ref_.video?.views > 0 && <span>· {ref_.video.views.toLocaleString()} views</span>}
            <a
              href={ref_.url}
              target="_blank"
              rel="noreferrer"
              className="text-zinc-400 hover:text-yellow-400 inline-flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          {ref_.status === "running" && (
            <div className="flex items-center gap-2 text-[11px] text-yellow-300 font-mono">
              <Loader2 className="w-3 h-3 animate-spin" />
              analyzing · {ref_.stage || "starting"}…
            </div>
          )}
          {ref_.status === "failed" && (
            <div className="text-[11px] text-red-400 font-mono">
              failed · {(ref_.error || "unknown error").slice(0, 160)}
            </div>
          )}
          {style.art_style && (
            <div className="text-[11px] text-yellow-300">
              <span className="font-mono uppercase tracking-wider text-zinc-500">style · </span>
              {style.art_style}
            </div>
          )}
          {palette.length > 0 && (
            <div className="flex items-center gap-1 pt-0.5">
              {palette.map((c, i) => (
                <span
                  key={`${c}-${i}`}
                  title={c}
                  style={{ background: c }}
                  className="w-4 h-4 rounded-full border border-zinc-800"
                />
              ))}
            </div>
          )}
        </div>
        <div className="flex-shrink-0 flex items-center gap-1">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="w-7 h-7 flex items-center justify-center rounded-sm text-zinc-500 hover:text-zinc-200 border border-transparent hover:border-zinc-700"
            title={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
          <button
            type="button"
            data-testid={`reference-delete-${ref_.reference_id}`}
            onClick={onDelete}
            className="no-pill w-7 h-7 flex items-center justify-center rounded-sm text-zinc-500 hover:text-red-400 border border-transparent hover:border-red-500/40 hover:bg-red-500/10"
            title="Remove from library"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      {expanded && (
        <div className="border-t border-zinc-800 p-3 space-y-3">
          {style.prompt_prefix && (
            <div className="border border-yellow-400/20 bg-yellow-400/5 rounded-sm p-3">
              <div className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-yellow-400 mb-1">
                Style prompt prefix
              </div>
              <div className="text-[11px] text-zinc-300 font-mono leading-relaxed">
                {style.prompt_prefix}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1.5">
                Auto-prepended to every scene image + thumbnail when you spawn a storyboard from a seed below.
              </div>
            </div>
          )}

          {seeds.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-yellow-400">
                  // Recast idea seeds ({availableSeeds.length} ready{consumedCount > 0 ? ` · ${consumedCount} built` : ""})
                </div>
                {ref_.status === "complete" && (
                  <button
                    type="button"
                    data-testid={`reference-generate-more-${ref_.reference_id}`}
                    onClick={handleGenerateMore}
                    disabled={generatingMore}
                    className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border-2 border-yellow-400/60 text-yellow-100 hover:text-black hover:bg-yellow-300 hover:border-yellow-300 bg-yellow-400/10 px-3 py-1.5 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Ask GPT-5.2 for 10 more recast idea seeds anchored on this reference"
                  >
                    {generatingMore ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Sparkles className="w-3 h-3" />
                    )}
                    {generatingMore ? "Generating…" : "Generate 10 more ideas"}
                  </button>
                )}
              </div>
              {availableSeeds.length === 0 && (
                <div
                  data-testid={`reference-seeds-empty-${ref_.reference_id}`}
                  className="border border-dashed border-zinc-800 rounded-sm p-4 font-mono text-[11px] text-zinc-400 text-center"
                >
                  Every seed has been spawned. Click{" "}
                  <span className="text-yellow-300 font-bold">Generate 10 more ideas</span>
                  {" "}above for a fresh batch.
                </div>
              )}
              {availableSeeds.map(({ seed: s, idx: i }) => (
                <div
                  key={`${ref_.reference_id}-seed-${i}`}
                  data-testid={`reference-seed-${ref_.reference_id}-${i}`}
                  className="border border-zinc-800 rounded-sm p-3 space-y-1.5 hover:border-zinc-600 transition-colors"
                >
                  <div className="text-sm font-bold text-zinc-100 leading-tight">
                    {s.title || `Seed ${i + 1}`}
                  </div>
                  {s.hook && <div className="text-xs text-yellow-300 italic">{s.hook}</div>}
                  {s.premise && (
                    <div className="text-[11px] text-zinc-400 leading-relaxed">
                      {s.premise}
                    </div>
                  )}
                  <div className="pt-1 flex items-center gap-2">
                    <button
                      type="button"
                      data-testid={`reference-spawn-${ref_.reference_id}-${i}`}
                      onClick={() => handleSpawn(i)}
                      disabled={spawningIdx === i}
                      className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] bg-yellow-400 hover:bg-yellow-300 text-black font-black px-3 py-1.5 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {spawningIdx === i ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Play className="w-3 h-3" />
                      )}
                      {spawningIdx === i ? "Spawning…" : "Spawn storyboard"}
                    </button>
                    {onDismissSeed && (
                      <button
                        type="button"
                        data-testid={`reference-seed-dismiss-${ref_.reference_id}-${i}`}
                        onClick={() => onDismissSeed(i)}
                        title="Not interested — hide this idea"
                        className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-red-300 border border-transparent hover:border-red-500/40 px-2.5 py-1.5 rounded-sm transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                        Dismiss
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {(ref_.transcript || "").length > 0 && (
            <details className="border border-zinc-800 rounded-sm">
              <summary className="cursor-pointer px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-400 hover:text-zinc-200">
                Transcript ({(ref_.transcript || "").length.toLocaleString()} chars)
              </summary>
              <div className="px-3 pb-3 text-[11px] text-zinc-400 leading-relaxed max-h-64 overflow-y-auto font-mono whitespace-pre-wrap">
                {ref_.transcript}
              </div>
            </details>
          )}
        </div>
      )}
    </article>
  );
}
